/*
 * Copyright 2019 Poly Forest, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Disposable } from '@amazon/vinyl-util'

/**
 * Generic interface for object pools.
 */
export interface Pool<T> {
    /**
     * Takes an object from the pool, or creates a new one if the pool is empty.
     */
    obtain(): T

    /**
     * Returns an object to the pool.
     */
    free(obj: T): void

    /**
     * Iterates over each item in the pool, invoking the provided callback.
     */
    forEach(callback: (obj: T) => void): void

    /**
     * Clears all free objects from the pool.
     * If the objects are disposable, consider using `disposeAndClear`.
     */
    clear(): void
}

/**
 * A simple object pool implementation with optional capacity and factory function.
 *
 * @param initialCapacity The initial array capacity for this pool.
 *                        Helps reduce resizes if many elements are expected.
 * @param capacity The maximum number of free objects the pool will retain.
 * @param create The factory function to create new instances when needed.
 */
export class ObjectPool<T> implements Pool<T>, Disposable {
    private readonly freeObjects: T[] = []

    constructor(
        private readonly create: () => T,
        initialCapacity: number = 8,
        private readonly capacity = 20000
    ) {
        this.freeObjects = new Array<T>(initialCapacity)
        this.freeObjects.length = 0 // Initialize with zero elements
    }

    obtain(): T {
        return this.freeObjects.length === 0 ? this.create() : this.freeObjects.pop()!
    }

    free(obj: T): void {
        if (this.freeObjects.length >= this.capacity) return
        this.freeObjects.push(obj)
    }

    forEach(callback: (obj: T) => void): void {
        for (let i = 0; i < this.freeObjects.length; i++) {
            callback(this.freeObjects[i])
        }
    }

    clear(): void {
        this.freeObjects.length = 0
    }

    dispose(): void {
        this.clear()
    }
}

/**
 * Frees all items in the given list back to the pool.
 */
export function freeAll<T>(pool: Pool<T>, list: readonly T[]): void {
    list.forEach((item) => pool.free(item))
}

/**
 * Frees all items in this list back to the given pool, then clears the list.
 */
export function freeTo<T>(list: T[], pool: Pool<T>): void {
    freeAll(pool, list)
    list.length = 0
}

/**
 * Disposes each object in this pool (must implement `Disposable`), then clears them.
 */
export function disposeAndClear<T extends Disposable>(pool: Pool<T>): void {
    pool.forEach((obj) => obj.dispose())
    pool.clear()
}
